import { useState, useCallback, useEffect } from 'react';
import { assetsAPI } from '@/api/endpoints';
import type { Asset, QuoteCurrency, AssetsSearchParams } from '@/api/types';

interface Pagination {
  currentPage: number;
  totalPages: number;
  total: number;
  perPage: number;
}

interface UseAssetsReturn {
  assets: Asset[];
  quoteCurrencies: QuoteCurrency[];
  pagination: Pagination;
  isLoading: boolean;
  error: string | null;
  searchAssets: (params?: AssetsSearchParams) => Promise<void>;
  loadMore: () => Promise<void>;
  clearAssets: () => void;
}

export const useAssets = (): UseAssetsReturn => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [quoteCurrencies, setQuoteCurrencies] = useState<QuoteCurrency[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSearch, setLastSearch] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 20,
  });

  const searchAssets = useCallback(async (params: AssetsSearchParams = {}): Promise<void> => {
    setIsLoading(true);
    setError(null);

    // Track search term for loadMore
    if (params.search !== undefined) {
      setLastSearch(params.search);
    }

    try {
      const response = await assetsAPI.search({
        page: params.page || 1,
        perPage: params.perPage || 20,
        search: params.search,
      });

      // If it's page 1, replace assets; otherwise append
      if (params.page === 1 || !params.page) {
        setAssets(response.assets);
      } else {
        setAssets((prev) => [...prev, ...response.assets]);
      }

      setPagination({
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        total: response.total,
        perPage: response.perPage,
      });
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to search assets';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async (): Promise<void> => {
    if (pagination.currentPage >= pagination.totalPages || isLoading) {
      return;
    }

    await searchAssets({
      page: pagination.currentPage + 1,
      search: lastSearch,
    });
  }, [pagination, isLoading, lastSearch, searchAssets]);

  const clearAssets = useCallback(() => {
    setAssets([]);
    setLastSearch('');
    setPagination({
      currentPage: 1,
      totalPages: 1,
      total: 0,
      perPage: 20,
    });
  }, []);

  // Fetch quote currencies on mount
  useEffect(() => {
    const fetchQuoteCurrencies = async () => {
      try {
        const response = await assetsAPI.getQuoteCurrencies();
        // API returns array directly or { currencies: [...] }
        const currencies = Array.isArray(response) ? response : response.currencies || [];
        setQuoteCurrencies(currencies);
      } catch (err) {
        console.error('Failed to fetch quote currencies:', err);
        // Fallback to default currencies
        setQuoteCurrencies([
          { id: '1', symbol: 'USDT', name: 'Tether' },
          { id: '2', symbol: 'EUR', name: 'Euro' },
          { id: '3', symbol: 'TRY', name: 'Turkish Lira' },
        ]);
      }
    };

    fetchQuoteCurrencies();
  }, []);

  return {
    assets,
    quoteCurrencies,
    pagination,
    isLoading,
    error,
    searchAssets,
    loadMore,
    clearAssets,
  };
};
